import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

if (!BACKEND_URL) {
  // Without this the base URL becomes the relative "undefined/api", every
  // request lands on the SPA redirect, and the app receives index.html with a
  // 200 instead of an error. CRA inlines this at build time, so a value added
  // after the fact needs a fresh deploy.
  console.error(
    "REACT_APP_BACKEND_URL is not set — the app cannot reach its API.\n" +
    "Set it to the backend origin (no trailing slash, no /api) and rebuild.\n" +
    "Netlify: Site configuration -> Environment variables, then redeploy."
  );
}

export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "slantscan_token";
const DEVICE_KEY = "slantscan_device";

/**
 * Storage that is allowed to not exist.
 *
 * Reading localStorage THROWS — it does not return null — in Safari's private
 * mode, in Chrome with third-party cookies blocked inside an iframe, and on any
 * page served from a non-secure origin. lib/theme.js has guarded this since it
 * was written; these three did not, and they are called from an axios request
 * interceptor, which is the worst possible place for it. A throw there rejects
 * every request the app will ever make, so a browser that merely cannot
 * remember you took the whole product down instead of signing you out.
 */
const readStored = (key) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};
const writeStored = (key, value) => {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch {
    // Won't survive a reload. Everything else still works.
    return false;
  }
};

// Same reasoning as sessionDevice below: when storage refuses a write, hold the
// value for this tab rather than dropping it. Without this, sign-in in Safari
// private mode "succeeded" -- setUser ran, the navbar showed an account -- and
// then getToken() returned null on every subsequent request, so the user was
// transparently anonymous for the whole session while being shown as signed in.
// Failing to persist is survivable; failing to persist silently is not.
let sessionToken = null;

// Another tab signing out removes the key from storage. Without this, a tab
// holding an unpersisted copy kept using it and stayed authenticated until
// reload — so the in-memory fallback would have quietly undone cross-tab
// logout, which used to take effect on the very next request.
if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
  window.addEventListener("storage", (e) => {
    if (e.key === TOKEN_KEY && e.newValue === null) sessionToken = null;
  });
}

export const getToken = () => readStored(TOKEN_KEY) ?? sessionToken;
export const setToken = (t) => {
  sessionToken = t;
  writeStored(TOKEN_KEY, t);
};
export const clearToken = () => {
  sessionToken = null;
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* nothing was stored to remove */
  }
};

/**
 * crypto.randomUUID is not universally there either: it is undefined on
 * insecure origins, and older Safari never shipped it. The device id is a
 * rate-limiting key, not a secret, so Math.random is an acceptable fallback —
 * what matters is that it is stable for the session and that asking for one
 * cannot throw.
 */
function randomId() {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through */
  }
  return `dev-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

// Holds the id for a browser that cannot persist one, so at least every request
// in this tab agrees about who is asking.
let sessionDevice = null;

function deviceId() {
  const stored = readStored(DEVICE_KEY);
  if (stored) return stored;
  if (!sessionDevice) sessionDevice = randomId();
  writeStored(DEVICE_KEY, sessionDevice);
  return sessionDevice;
}

const client = axios.create({ baseURL: API, timeout: 120000 });
client.interceptors.request.use((config) => {
  // Belt and braces around the helpers above: nothing this interceptor does is
  // worth failing a request over, and a request without these headers is a
  // logged-out request rather than a broken one.
  try {
    const t = getToken();
    if (t) config.headers.Authorization = `Bearer ${t}`;
    config.headers["X-Device-Id"] = deviceId();
  } catch {
    /* degrade to an anonymous, un-deduplicated request */
  }
  return config;
});

// Every endpoint here returns JSON (the share card is an <img>, not an axios
// call). A misrouted request still resolves 200 because the SPA redirect serves
// index.html for anything unmatched — so without this check the callers below
// get an HTML string where they expect an array or object, and the first
// .map() on it takes the whole app down with a blank page. Reject it instead,
// which the existing error handling already copes with.
client.interceptors.response.use((r) => {
  const type = r.headers?.["content-type"] || "";
  if (!type.includes("application/json")) {
    throw new Error(
      `Expected JSON from ${r.config?.url} but received "${type || "unknown"}". ` +
      "This usually means REACT_APP_BACKEND_URL is wrong or unset, so the " +
      "request was served by the frontend instead of the API."
    );
  }
  return r;
});

// auth
export const registerUser = (email, password) => client.post("/auth/register", { email, password }).then((r) => r.data);
export const loginUser = (email, password) => client.post("/auth/login", { email, password }).then((r) => r.data);
export const fetchMe = () => client.get("/auth/me").then((r) => r.data);
export const requestPasswordReset = (email) => client.post("/auth/forgot-password", { email }).then((r) => r.data);
export const resetPassword = (token, password) => client.post("/auth/reset-password", { token, password }).then((r) => r.data);
export const changePassword = (current_password, new_password) =>
  client.post("/auth/change-password", { current_password, new_password }).then((r) => r.data);
export const deleteAccount = (confirm) => client.post("/auth/delete-account", { confirm }).then((r) => r.data);

export const reportCardUrl = (id) => `${API}/report/${id}/card.png`;

// core
export const analyzeArticle = (payload) => client.post("/analyze", payload).then((r) => r.data);
export const getReport = (id) => client.get(`/report/${id}`).then((r) => r.data);
export const getHistory = () => client.get(`/history`).then((r) => r.data);
export const getRecent = (limit = 8) => client.get(`/recent`, { params: { limit } }).then((r) => r.data);
export const getUsage = () => client.get(`/usage`).then((r) => r.data);
export const compareArticles = (payload) => client.post("/compare", payload).then((r) => r.data);
export const joinWaitlist = (email) => client.post("/waitlist", { email }).then((r) => r.data);

// live feed (ingested articles). Reading is always free; only analyzeFeedArticle
// on a not-yet-scored article costs the caller a scan.
export const getTicker = (limit = 40, source) =>
  client.get("/feed/ticker", { params: { limit, ...(source ? { source } : {}) } }).then((r) => r.data);
export const getFeedArticle = (id) => client.get(`/feed/${id}`).then((r) => r.data);
export const getFeedSources = () => client.get("/feed/sources").then((r) => r.data);
// Resolves { status: "scored", report_id } — or { status: "scoring" } with a 202
// when somebody else claimed the same article a moment ago.
export const analyzeFeedArticle = (id) => client.post(`/feed/${id}/analyze`).then((r) => r.data);

// outlets. All three are public; an authenticated Pro caller additionally
// receives `trend` and `top_phrases` on a profile, which is why nothing here
// takes a plan — the bearer the interceptor already attaches is what the server
// gates on. See docs/api-outlets.md.
//
// The domain is encoded because it arrives from useParams(), which hands back a
// DECODED path segment: anything the router matched but the API cannot place
// ("foo/bar", a stray "?") would otherwise change which route is being called
// rather than 404 as an outlet that does not exist.
export const getOutlets = (minScans) =>
  client.get("/outlets", { params: minScans == null ? {} : { min_scans: minScans } }).then((r) => r.data);
export const getOutlet = (domain) => client.get(`/outlets/${encodeURIComponent(domain)}`).then((r) => r.data);
// Deliberately never 404s: an outlet with no scans answers 200 with an empty
// `points`, because one of these is requested per report card and a page of
// working cards should not become a page of failed requests.
export const getOutletSparkline = (domain, days = 90) =>
  client.get(`/outlets/${encodeURIComponent(domain)}/sparkline`, { params: { days } }).then((r) => r.data);

// personal API tokens. Session-JWT only, deliberately: a leaked `ss_` token
// cannot mint itself a spare that would survive its own revocation.
export const getTokens = () => client.get("/tokens").then((r) => r.data);
// The raw token comes back in this response and in no other, ever again.
export const createToken = (label) => client.post("/tokens", { label }).then((r) => r.data);
export const revokeToken = (id) => client.delete(`/tokens/${id}`).then((r) => r.data);

// billing (Polar)
export const startCheckout = () => client.post("/billing/checkout").then((r) => r.data);
export const openBillingPortal = () => client.post("/billing/portal").then((r) => r.data);

// Blue-left / red-right is the reading itself, so the hue is fixed in both
// themes; only the lightness inverts — a tinted paper chip becomes a tinted ink
// one. Center gets the neutral tokens, because "no lean" should look like the
// absence of a tint rather than a grey of its own.
export const LEAN_COLORS = {
  Left: {
    text: "text-blue-700 dark:text-blue-300",
    bg: "bg-blue-100 dark:bg-blue-500/15",
    border: "border-blue-300 dark:border-blue-500/40",
  },
  "Lean Left": {
    text: "text-blue-600 dark:text-blue-300",
    bg: "bg-blue-50 dark:bg-blue-500/10",
    border: "border-blue-200 dark:border-blue-500/25",
  },
  Center: { text: "text-dim", bg: "bg-subtle", border: "border-line/25" },
  "Lean Right": {
    text: "text-red-600 dark:text-red-300",
    bg: "bg-red-50 dark:bg-red-500/10",
    border: "border-red-200 dark:border-red-500/25",
  },
  Right: {
    text: "text-red-700 dark:text-red-300",
    bg: "bg-red-100 dark:bg-red-500/15",
    border: "border-red-300 dark:border-red-500/40",
  },
};
export const leanStyle = (label) => LEAN_COLORS[label] || LEAN_COLORS.Center;

/**
 * The five labels the scorer emits, left to right.
 *
 * One list, in reading order, rather than Object.keys(LEAN_COLORS) taken at the
 * call site — History's filter chips derived their order from key insertion
 * order in the object literal above, which is a property nothing was defending.
 * src/__tests__/contract.test.js asserts this and LEAN_COLORS still describe
 * the same five labels in the same order.
 */
export const LEAN_LABELS = Object.freeze([
  "Left",
  "Lean Left",
  "Center",
  "Lean Right",
  "Right",
]);

/**
 * Which of the five labels a bare score falls under.
 *
 * The scorer stamps `lean_label` on every report, so nothing needed this until
 * the outlet endpoints started returning an average with no label beside it.
 * The bins are the backend's, mirrored exactly — docs/api-outlets.md, "Bins and
 * labels": [-10,-6) [-6,-2) [-2,2] (2,6] (6,10]. The centre bin is closed on
 * both sides and every other boundary follows from that, so a score of exactly
 * -2 is Lean Left here and Lean Left in the histogram the server built.
 *
 * Centre for a non-finite score, for the same reason lib/gauge.js parks the
 * marker there: an outlet whose average is null (no scans) has no reading, and
 * "no lean" is the honest way to draw one.
 */
export function leanLabel(score) {
  const n = Number(score);
  if (!Number.isFinite(n)) return LEAN_LABELS[2];
  if (n < -6) return LEAN_LABELS[0];
  if (n < -2) return LEAN_LABELS[1];
  if (n <= 2) return LEAN_LABELS[2];
  if (n <= 6) return LEAN_LABELS[3];
  return LEAN_LABELS[4];
}

/**
 * The `detail.code` values the frontend branches on.
 *
 * Frozen and named because there were two branch tables over this one set —
 * AnalyzeBox and ArticlePeekDialog — shaped differently and agreeing only by
 * luck. Both read from here now.
 */
export const ERROR_CODES = Object.freeze({
  SIGNUP_REQUIRED: "signup_required",
  LIMIT_REACHED: "limit_reached",
  CAPACITY: "capacity",
  // The outlet profile's 404. Branched on rather than read off the status,
  // because the same route also 404s a domain it cannot place at all and the
  // contract is explicit that the two are told apart by `code` and never by
  // `message` — the message differs by cause and is not a stable string.
  OUTLET_NOT_FOUND: "outlet_not_found",
});

/**
 * Codes the backend emits that NOTHING here matches — they fall through to the
 * generic "couldn't analyze that" path and show the server's message.
 *
 * Listed rather than handled because handling them is a product decision, not a
 * refactor: `pro_required` wants the upgrade wall, `rate_limited` wants a retry
 * hint, `too_many_logins` wants to say so on the sign-in form. What this
 * constant buys today is that the gap is written down and counted by a test,
 * instead of being discovered again the next time someone reads the backend.
 */
export const UNHANDLED_ERROR_CODES = Object.freeze([
  "rate_limited",
  "pro_required",
  "unavailable",
  "too_many_logins",
  "reset_invalid",
  "no_password",
  // The three the token settings meet. Listed rather than branched on because
  // the server's own wording is already the whole answer in each case — "You
  // already have 10 active tokens" says the limit and what to do about it, and
  // a client-side copy of the number 10 would be one more thing to keep in step
  // with the backend for no gain. They surface through errDetail's message.
  "invalid_label",
  "token_limit",
  "token_not_found",
]);

export function errDetail(e) {
  const d = e?.response?.data?.detail;
  if (d == null) return { message: "Something went wrong. Please try again." };
  if (typeof d === "string") return { message: d };
  if (Array.isArray(d)) return { message: d.map((x) => x.msg || "").join(" ") };
  return d; // {code, message}
}
